import { Journey } from '@audibene/ta.utils-opportunity'
import { isValid } from 'date-fns'
import { useFilteredAppointmentTypeOptions } from 'hooks/useFilteredAppointmentTypeOptions'
import { useEffect, useRef } from 'react'
import {
  AcfuProgressiveDisclosureParameters,
  ANY_RESOURCE_ID,
  AppointmentParameters,
  AppointmentType,
  CalendarIntegrationType,
  Country,
  appointmentDurations,
  getRequiredAppointmentParameters,
  isShopGidOptionalForPrsFlow,
} from '../../constants'
import { AppointmentFormData, Customer } from '../../types'
import { appointmentToTitle, formDataToStartDate, getIsCabinOnlyConfiguration } from '../../util'
import { useAppointmentLocationState } from '../useAppointmentLocationState'
import { useAppointmentTypes } from '../useAppointmentTypes'
import { useBlockedAppointmentType } from '../useBlockedAppointmentType'
import { useEventTracking } from '../useEventTracking'
import { getOfferedSubstituteAppointmentType, getTrialGate } from '../useTrialGate/trialGates'
import { useCalendarIntegrationsSummaries } from '../useCalendarIntegrationsSummaries'
import { useCustomer } from '../useCustomer'
import { useFeatureFlags } from '../useFeatureFlags'
import { useNow } from '../useNow'
import { useRescheduleContext } from '../useRescheduleContext'
import { SchedulingStatus, useSchedulingState } from '../useSchedulingState'
import { useSearchParameters } from '../useSearchParameters'
import { useShop } from '../useShop'
import { useTimeZones } from '../useTimeZones'
import { useCountry } from '../useCountry'

type AppointmentTypeKey = keyof typeof AppointmentType

const parseAppointmentType = (value?: string): AppointmentType =>
  AppointmentType[value as AppointmentTypeKey] ?? AppointmentType.HEARING_AID_EVALUATION

const startDateRegEx = new RegExp(/^\d{4}-\d{2}-\d{2}$/)
const startTimeRegEx = new RegExp(/^\d{2}:\d{2}$/)

const parseCountry = (value?: string): Country | undefined => Country[value as Country] ?? undefined
const parseJourney = (value?: string): Journey | undefined => Journey[value as Journey] ?? undefined

const parseStartDate = (value?: string): Date | null => {
  if (!startDateRegEx.test(value ?? '')) return null

  const [year, month, day] = (value as string).split('-').map(Number)

  const date = new Date(year, month - 1, day)

  if (!isValid(date)) return null

  return date
}

const parseStartTime = (value?: string): Date | null => {
  if (!startTimeRegEx.test(value ?? '')) return null

  const [hour, minute] = (value as string).split(':').map(Number)

  if (hour < 0 || hour > 23) return null
  if (minute < 0 || minute > 59) return null

  const date = new Date(0, 0, 0)

  date.setHours(hour)
  date.setMinutes(minute)

  return date
}

const toSafeString = (value: any) => value ?? ''

// Needed because React doesn't like when we set form values to undefined or null.
// It throws warnings about controlled and uncontrolled otherwise.
const toSafeInputValues = (appointment: AppointmentFormData): AppointmentFormData =>
  Object.entries(appointment).reduce<AppointmentFormData>(
    (acc, [key, value]) => ({
      ...acc,
      [key]: ['clientStartDate', 'clientStartTime', 'start_date'].includes(key) ? value : toSafeString(value),
    }),
    {}
  )

const useScheduleFormDefaultValues = () => {
  const country = useCountry()
  const searchParameters = useSearchParameters()
  const appointmentLocationState = useAppointmentLocationState()
  const { customer, isLoading: isCustomerLoading } = useCustomer()
  const { shop, isLoading: isResourceLoading } = useShop()
  const { appointmentTypes, isLoading: areAppointmentTypesLoading } = useAppointmentTypes()
  const { timeZones, isLoading: areTimeZonesLoading } = useTimeZones()
  const { data: now } = useNow()
  const { setStatus: setSchedulingStatus } = useSchedulingState()
  const [isShopDeliveryModelConfigurationEnabled] = useFeatureFlags<boolean>(['ENABLE_SHOP_DELIVERY_MODEL_CONFIGURATION'])
  const { blockedAppointmentTypes, isLoading: isTrialGateLoading } = useBlockedAppointmentType()
  const rescheduleContext = useRescheduleContext()
  const isRescheduling = Boolean(rescheduleContext?.reason)
  const { track: trackEvent } = useEventTracking()

  const appointmentParameters = {
    ...searchParameters,
    ...appointmentLocationState,
    country,
  }

  const { appointment_type, journey, opportunity_gid, start_date, start_time } = appointmentParameters
  const isCabinOnlyConfiguration = Boolean(isShopDeliveryModelConfigurationEnabled && getIsCabinOnlyConfiguration(shop, journey as string))

  const shopIds = shop?.id ? [shop.id] : []
  const { data: calendarSummaries, isLoading: areSummariesLoading } = useCalendarIntegrationsSummaries(shopIds, {
    enabled: isCabinOnlyConfiguration,
  })

  const isLoading =
    isCustomerLoading ||
    isResourceLoading ||
    areAppointmentTypesLoading ||
    areTimeZonesLoading ||
    isTrialGateLoading ||
    (isCabinOnlyConfiguration && areSummariesLoading)
  const isParameterMissing = (parameter: AppointmentParameters) => typeof appointmentParameters?.[parameter] === 'undefined'
  const invalidParameters = getRequiredAppointmentParameters(journey as string, country as string).filter(isParameterMissing)

  const { shouldRestrictToHAE } = useFilteredAppointmentTypeOptions(journey)

  // Hoisted above the early returns below so the substitution decision is available to the effect
  // that reports it - the values it reads are all resolved by the time isLoading clears.
  const requestedType = shouldRestrictToHAE ? AppointmentType.HEARING_AID_EVALUATION : parseAppointmentType(appointment_type as string)
  // If the URL/location-state requested a type this customer's trial status blocks (PX-1807,
  // PX-3749), silently substitute the replacement instead of defaulting the form to a disabled
  // option - but never during a reschedule, where `appointment_type` is the *existing*
  // appointment's own type: substituting there would silently reclassify it (e.g. Follow-up ->
  // Aftercare Follow-up) instead of just steering a new booking away from a disabled option.
  //
  // The helper pairs the lookup with the shop's own configuration: a replacement the shop does not
  // offer is worse than the blocked type, which at least renders disabled with a coherent reason.
  const substituteType = isRescheduling
    ? undefined
    : getOfferedSubstituteAppointmentType(requestedType, blockedAppointmentTypes, appointmentTypes)
  const isAppointmentTypeSubstituted = Boolean(substituteType)

  useEffect(() => {
    if (isLoading) return

    setSchedulingStatus(SchedulingStatus.FETCHING)
  }, [isLoading, setSchedulingStatus])

  // useEventTracking hands back a new identity every render; a ref keeps this effect's dependencies
  // exhaustive without re-firing on every render.
  const trackEventRef = useRef(trackEvent)
  trackEventRef.current = trackEvent

  // Reported here rather than from the surfaces, because this is where the booking actually changes
  // and it is the only point that sees every substitution. The disabled-option impression cannot
  // stand in for it: a read-only surface (a resource_id deep link) shows no pickable option, so it
  // correctly reports no impression, yet that is exactly where the gate has taken its strongest
  // action - the type was changed and the user cannot change it back.
  const substitutedFromType = isAppointmentTypeSubstituted && !isLoading ? requestedType : null

  useEffect(() => {
    if (!substitutedFromType) return

    const substitutionAppliedEvent = getTrialGate(substitutedFromType)?.substitutionAppliedEvent
    if (!substitutionAppliedEvent) return

    trackEventRef.current(substitutionAppliedEvent)
  }, [substitutedFromType])

  if (isLoading) {
    return null
  }

  if (invalidParameters.length) {
    return null
  }

  const missingDataErrors = Object.entries({
    appointmentTypes: !appointmentTypes && `Could not load appointment types: ${country} / ${journey}`,
    customer: !customer && `Could not find a customer with gid: ${appointmentParameters.customer_gid}`,
    shop:
      !shop &&
      !isShopGidOptionalForPrsFlow(journey as string, country as string) &&
      `Could not find a shop${appointmentParameters.shop_gid ? ` with gid: ${appointmentParameters.shop_gid}` : ''}`,
    timeZones: !timeZones && `Could not load time zones`,
  })
    .filter(([, value]) => Boolean(value))
    .reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: value as string,
      }),
      {}
    )

  if (Object.keys(missingDataErrors).length > 0) {
    return null
  }

  const hasSingleResource = shop?.resources?.length === 1
  const { resource_id, resource_ids: resourceIdsFromRouter } = appointmentParameters

  const resourceIdsFromUrl = resource_id ? (Array.isArray(resource_id) ? resource_id : [resource_id]) : []

  const wantedResourceIds = resourceIdsFromRouter?.length ? resourceIdsFromRouter : resourceIdsFromUrl

  const resource = shop?.resources?.find(({ id }) => wantedResourceIds.includes(id))
  const clientStartDate = parseStartDate(start_date as string)
  const clientStartTime = parseStartTime(start_time as string)
  const type = substituteType ?? requestedType

  const hasCabinAgnosticIntegration =
    calendarSummaries?.[shop?.id as string]?.integration_types?.includes(CalendarIntegrationType.ASEGO) ?? false
  const isCabinSelectionDisabled = isCabinOnlyConfiguration && hasCabinAgnosticIntegration

  // country is set here once during form initialization, sourced from useCountry(),
  // which derives it from the first URL path segment and falls back to US.
  // It is read-only throughout the form lifecycle — there is no field or setValue call
  // that changes it. Consumers access it via useWatch({ name: 'country' }).
  const defaultValues: AppointmentFormData = toSafeInputValues({
    acfuExtendedWindowReason: null,
    acfuRequiresExtendedWindowReason: false,
    acfuShowMoreClickCount: 0,
    acfuVisibleWindowDays: AcfuProgressiveDisclosureParameters.DEFAULT_WINDOW_IN_DAYS,
    clientStartDate,
    clientStartTime,
    country: parseCountry(country as string),
    customer_salesforce_id: customer?.salesforceId,
    customer_timezone: customer?.timezone,
    description: null,
    duration_in_minutes: appointmentTypes?.[type]?.duration_in_minutes,
    duration_in_minutes_temp: appointmentDurations.UNKNOWN,
    fitter_timezone: hasSingleResource ? resource?.timezone : null,
    isCabinSelectionDisabled,
    journey: parseJourney(journey as string),
    month_date: now,
    opportunity_gid: opportunity_gid as string,
    resource_ids: isCabinSelectionDisabled ? [ANY_RESOURCE_ID] : wantedResourceIds,
    specialist_id: isCabinSelectionDisabled ? ANY_RESOURCE_ID : resource?.id ?? '',
    start_date: formDataToStartDate({
      clientStartDate,
      clientStartTime,
      customer_timezone: customer?.timezone,
    }),
    title: appointmentToTitle({ customer: customer as Customer, type }),
    type,
  })

  return defaultValues
}

export { parseAppointmentType, useScheduleFormDefaultValues }
